import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Button, Form, Icon, Input, Message } from 'semantic-ui-react';
import styled from 'styled-components';
import { updateWritingList } from 'src/modules/book';
import { authUtil } from 'src/utils';
import {
  CritiqueFeedbackVisibility,
  CritiqueRevealPolicy,
  useCreateCritiqueGroup,
} from 'src/hooks/useCritiqueGroups';
import { bookRefCoverUrl, FREE_TASTE } from 'src/utils/critiqueGroupUtil';
import { BookSpine } from 'src/components/critiqueGroups/bookSpine';
import { MediaQuery } from 'src/constants/size';
import { TierGateModal, TierLimit } from 'src/components/critiqueGroups/tierGate';
import { useCanHostCritiqueGroups } from './useCanHostCritiqueGroups';

/** Design B1 — name, book(s), feedback visibility, with the tier check inline. */

const Panel = styled.div`
  background: var(--br-surface);
  border: 1px solid #e2ddd0;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(45, 40, 25, 0.06);
`;

const PanelHead = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 22px;
  border-bottom: 1px solid #eee7da;
  background: var(--br-raised-surface);
`;

const Body = styled.div`
  padding: 26px 30px;
  display: flex;
  flex-direction: column;
  gap: 26px;
  ${MediaQuery.phone} {
    padding: 20px 14px;
    gap: 20px;
  }
`;

const Foot = styled.div`
  padding: 16px 30px;
  border-top: 1px solid #eee7da;
  background: var(--br-raised-surface);
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
  ${MediaQuery.phone} {
    padding: 14px;
  }
`;

const FieldLabel = styled.label`
  display: block;
  font-weight: 800;
  font-size: 13.5px;
  margin-bottom: 7px;
`;

const Hint = styled.span`
  font-weight: 400;
  color: #a49f93;
`;

const BookGrid = styled.div`
  display: grid;
  /* auto-fill adapts the track COUNT, never the 220px floor, so below ~220px of
     available width a bare minmax(220px, 1fr) overflows. min() lets the single
     column collapse to the container on a narrow phone. */
  grid-template-columns: repeat(auto-fill, minmax(min(220px, 100%), 1fr));
  gap: 12px;
`;

const Choice = styled.div<{ $selected: boolean; $disabled?: boolean }>`
  border: ${({ $selected }) =>
    $selected ? '2px solid #00b5ad' : '1.5px solid #e7e3d9'};
  background: ${({ $selected }) => ($selected ? '#f2fbfa' : '#fff')};
  border-radius: 12px;
  padding: 12px;
  display: flex;
  gap: 12px;
  align-items: center;
  position: relative;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
`;

const Radio = styled.span<{ $on: boolean }>`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 5px solid ${({ $on }) => ($on ? '#00b5ad' : '#d0cabb')};
  margin-top: 2px;
  flex-shrink: 0;
`;

const Option = styled.div<{ $selected: boolean }>`
  border: ${({ $selected }) =>
    $selected ? '2px solid #00b5ad' : '1.5px solid #e7e3d9'};
  background: ${({ $selected }) => ($selected ? '#f2fbfa' : '#fff')};
  border-radius: 12px;
  padding: 13px 15px;
  display: flex;
  gap: 12px;
  align-items: flex-start;
  cursor: pointer;
`;

const SubOptions = styled.div`
  margin-top: 11px;
  padding-top: 11px;
  border-top: 1px dashed #cdeae7;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

/** Maps the server's upsell codes onto which limit to explain in the gate. */
const limitFromCode = (code?: string): TierLimit | undefined => {
  if (code === 'UPGRADE_REQUIRED_MAX_GROUPS') return 'groups';
  if (code === 'UPGRADE_REQUIRED_MAX_BOOKS') return 'books';
  if (code === 'UPGRADE_REQUIRED_PUBLIC_GROUP') return 'public';
  if (code === 'MEMBER_CAP_REACHED') return 'members';
  return undefined;
};

const CreateCritiqueGroup: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const userProfile = useSelector((state: any) => state.user.userProfile);
  const activeAccount = useSelector((state: any) => state.account.activeAccount);
  const writing = useSelector((state: any) => state.book.writing);
  const { canHost } = useCanHostCritiqueGroups();
  const createGroup = useCreateCritiqueGroup();

  const [name, setName] = useState('');
  const [selectedBooks, setSelectedBooks] = useState<string[]>([]);
  const [feedbackVisibility, setFeedbackVisibility] =
    useState<CritiqueFeedbackVisibility>('members_shared');
  const [revealPolicy, setRevealPolicy] = useState<CritiqueRevealPolicy>('after_own');
  const [gate, setGate] = useState<TierLimit | undefined>();

  // Reuse the existing writing-list thunk rather than a parallel fetch, so the
  // picker shows exactly the books "My manuscripts" does.
  useEffect(() => {
    const load = async () => {
      const idToken = await authUtil.getFreshIdToken();
      if (idToken && userProfile) {
        dispatch(
          updateWritingList({
            idToken,
            accountId: activeAccount ? activeAccount._id : userProfile.account?._id,
          })
        );
      }
    };
    load();
  }, [activeAccount, userProfile?._id]);

  const books = useMemo(() => writing || [], [writing]);
  const maxBooks = canHost ? Infinity : FREE_TASTE.maxBooks;
  // At the linked-book limit (a paid host never is). Single source of truth for
  // both the per-book "blocked" state and the second-book upsell, so they can't
  // drift — the upsell must appear exactly when a further pick is blocked.
  const atBookLimit = selectedBooks.length >= maxBooks;

  const toggleBook = (bookId: string) => {
    setSelectedBooks(prev => {
      if (prev.includes(bookId)) return prev.filter(b => b !== bookId);
      if (prev.length >= maxBooks) {
        // Free host reaching for a second book — explain rather than silently
        // ignore the click.
        setGate('books');
        return prev;
      }
      return [...prev, bookId];
    });
  };

  const submit = () => {
    createGroup.mutate(
      {
        name: name.trim(),
        books: selectedBooks,
        feedbackVisibility,
        // revealPolicy is meaningless under host_only; send it anyway so the
        // stored setting survives a later switch back to members_shared.
        revealPolicy,
      },
      {
        onSuccess: group => history.push(`/critique-groups/${group._id}`),
        onError: err => {
          const limit = limitFromCode(err.code);
          if (limit) setGate(limit);
        },
      }
    );
  };

  const nameError = name.trim().length === 0;
  // The server allows a bookless group, but there's no UI to link one later, so
  // it would be a dead end: no reading, no feedback, no checkpoint targets.
  // Require a book here whenever the author actually has one to pick.
  const bookError = books.length > 0 && selectedBooks.length === 0;
  const serverError = createGroup.error;
  // A tier error is handled by the modal — don't also show it as a red banner.
  const showInlineError = serverError && !limitFromCode(serverError.code);

  return (
    <div data-testid="create-critique-group">
      <Panel>
        <PanelHead>
          <Icon
            name="arrow left"
            link
            style={{ color: '#8a857b' }}
            onClick={() => history.push('/critique-groups')}
            data-testid="create-back"
          />
          <span style={{ fontWeight: 800, fontSize: 16 }}>
            {t('CritiqueNewGroup', 'New critique group')}
          </span>
        </PanelHead>

        <Body>
          <div>
            <FieldLabel htmlFor="cg-name">
              {t('CritiqueGroupName', 'Group name')}
            </FieldLabel>
            <Input
              id="cg-name"
              fluid
              value={name}
              onChange={(_e, d) => setName(d.value)}
              placeholder={t('CritiqueGroupNamePlaceholder', 'e.g. Lighthouse Circle')}
              data-testid="create-name-input"
            />
          </div>

          <div>
            <FieldLabel>
              {t('CritiqueWhichManuscripts', 'Which manuscript(s)?')}{' '}
              {!canHost && (
                <Hint>
                  ·{' '}
                  {t('CritiqueFreePlanOneBook', 'free plan: {{count}} book', {
                    count: FREE_TASTE.maxBooks,
                  })}
                </Hint>
              )}
            </FieldLabel>
            {books.length === 0 ? (
              <Message info size="small" data-testid="create-no-books">
                {t(
                  'CritiqueNoBooksYet',
                  'You don’t have a manuscript yet. Create one first, then host a circle around it.'
                )}
              </Message>
            ) : (
              <BookGrid>
                {books.map((b: any) => {
                  const selected = selectedBooks.includes(b._id);
                  const blocked = !selected && atBookLimit;
                  return (
                    <Choice
                      key={b._id}
                      $selected={selected}
                      $disabled={blocked}
                      onClick={() => toggleBook(b._id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') toggleBook(b._id);
                      }}
                      data-testid={`create-book-${b._id}`}
                    >
                      <BookSpine
                        title={b.title}
                        coverUrl={bookRefCoverUrl(b)}
                        seed={b._id}
                        width={40}
                        height={56}
                      />
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            fontWeight: 800,
                            fontSize: 14,
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere',
                          }}
                        >
                          {b.title}
                        </div>
                        <div style={{ fontSize: 12, color: '#8a857b' }}>
                          {t('CritiqueNChapters', '{{count}} chapters', {
                            count: b.parts?.length ?? b.chapterCount ?? 0,
                          })}
                        </div>
                      </div>
                      {selected && (
                        <Icon
                          name="check circle"
                          style={{ position: 'absolute', top: 8, right: 10, color: '#00b5ad' }}
                        />
                      )}
                    </Choice>
                  );
                })}
              </BookGrid>
            )}
            {/* Only surface the second-book upsell once a free host has actually
                used their one free book — showing it at the empty/first-pick
                state read as "you can't even pick your first book", which is
                wrong (the first book is always selectable). It's now relevant
                exactly when a further pick would be blocked. */}
            {!canHost && atBookLimit && (
              <div style={{ marginTop: 8, fontSize: 12, color: '#c85f14' }}>
                <Icon name="lock" />
                {t('CritiqueSecondBookPremium', 'Add a second book with Premium.')}
              </div>
            )}
          </div>

          <div>
            <FieldLabel>{t('CritiqueFeedbackVisibility', 'Feedback visibility')}</FieldLabel>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <Option
                $selected={feedbackVisibility === 'host_only'}
                onClick={() => setFeedbackVisibility('host_only')}
                data-testid="create-visibility-host-only"
              >
                <Radio $on={feedbackVisibility === 'host_only'} />
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>
                    {t('CritiqueVisHostOnly', 'Only me — feedback comes to you privately')}
                  </div>
                  <div style={{ fontSize: 12.5, color: '#8a857b' }}>
                    {t('CritiqueVisHostOnlyBody', 'Members give feedback but never see each other’s.')}
                  </div>
                </div>
              </Option>

              <Option
                $selected={feedbackVisibility === 'members_shared'}
                onClick={() => setFeedbackVisibility('members_shared')}
                data-testid="create-visibility-shared"
              >
                <Radio $on={feedbackVisibility === 'members_shared'} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>
                    {t('CritiqueVisShared', 'Members can see each other’s')}{' '}
                    <span className="ui teal label" style={{ borderRadius: 999, fontSize: 10, padding: '3px 8px' }}>
                      {t('Default', 'Default')}
                    </span>
                  </div>
                  <div style={{ fontSize: 12.5, color: '#8a857b' }}>
                    {t('CritiqueVisSharedBody', 'A shared conversation — the writing-family feel.')}
                  </div>

                  {feedbackVisibility === 'members_shared' && (
                    <SubOptions
                      onClick={e => e.stopPropagation()}
                      data-testid="create-reveal-options"
                    >
                      <label
                        style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, cursor: 'pointer' }}
                        onClick={() => setRevealPolicy('immediate')}
                      >
                        <Radio $on={revealPolicy === 'immediate'} style={{ width: 15, height: 15, borderWidth: 4 }} />
                        {t('CritiqueRevealImmediate', 'Immediately')}
                      </label>
                      <label
                        style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, cursor: 'pointer' }}
                        onClick={() => setRevealPolicy('after_own')}
                        data-testid="create-reveal-after-own"
                      >
                        <Radio $on={revealPolicy === 'after_own'} style={{ width: 15, height: 15, borderWidth: 4 }} />
                        <span style={{ fontWeight: revealPolicy === 'after_own' ? 700 : 400 }}>
                          {t('CritiqueRevealAfterOwn', 'Only after they’ve shared their own')}
                        </span>
                        <span style={{ fontSize: 11, color: '#00817b' }}>
                          {t('CritiqueRevealAfterOwnWhy', '— reduces anchoring, honest first impressions')}
                        </span>
                      </label>
                    </SubOptions>
                  )}
                </div>
              </Option>
            </div>
          </div>

          <div style={{ fontSize: 12.5, color: '#8a857b' }}>
            <Icon name="info circle" />
            {t(
              'CritiqueCheckpointsLater',
              'You can add reading checkpoints once the group exists.'
            )}
          </div>

          {showInlineError && (
            <Message negative size="small" data-testid="create-error">
              {serverError?.message}
            </Message>
          )}
        </Body>

        <Foot>
          <Button basic onClick={() => history.push('/critique-groups')} style={{ borderRadius: 9 }}>
            {t('Cancel', 'Cancel')}
          </Button>
          <Button
            color="teal"
            onClick={submit}
            disabled={nameError || bookError || createGroup.isLoading}
            loading={createGroup.isLoading}
            style={{ borderRadius: 9, fontWeight: 700 }}
            data-testid="create-submit"
          >
            {t('CritiqueCreateAndInvite', 'Create group & invite readers')}
          </Button>
        </Foot>
      </Panel>

      <TierGateModal
        open={!!gate}
        limit={gate}
        serverMessage={
          gate && limitFromCode(createGroup.error?.code) === gate
            ? createGroup.error?.message
            : undefined
        }
        onClose={() => setGate(undefined)}
        onUpgrade={() => history.push('/account/subscription')}
      />
    </div>
  );
};

export default CreateCritiqueGroup;
